import { resolveEdgeFromApiVersions } from '@/utility/apiVersionMatrix'
import { markOrgSwitchTransition, ORG_SWITCH_STORAGE_KEY } from './orgSwitchStorage'
import { clearLocalStorage, createClerkSupabaseClientLandingZone } from './utility'

export const IMPERSONATION_TARGET_ORG_SESSION_KEY = 'impersonation-target-org-id'

const INVALID_ORG_IDS = new Set([null, undefined, '', 'undefined', 'null'])

export function getImpersonationTargetOrgId() {
  const value = sessionStorage.getItem(IMPERSONATION_TARGET_ORG_SESSION_KEY)
  if (INVALID_ORG_IDS.has(value)) {
    return null
  }
  return value
}

export function setImpersonationTargetOrgId(organizationId) {
  if (INVALID_ORG_IDS.has(organizationId)) {
    return
  }
  sessionStorage.setItem(IMPERSONATION_TARGET_ORG_SESSION_KEY, organizationId)
}

export function clearImpersonationTargetOrgId() {
  sessionStorage.removeItem(IMPERSONATION_TARGET_ORG_SESSION_KEY)
}

const createActorToken = async (session, user_id, actor_id) => {
  const client = createClerkSupabaseClientLandingZone(session)

  const { data, error: tokenError } = await client.functions.invoke(
    resolveEdgeFromApiVersions('create-actor-token-v1'),
    {
      headers: {
        'content-type': 'application/json',
      },
      body: {
        user_id: actor_id,
        actor_data: {
          sub: user_id,
        },
      },
    },
  )

  if (tokenError) {
    throw new Error(data?.errors?.[0]?.long_message ?? 'Failed to generate actor token')
  }

  return data
}

const clearShipmentFilterStorage = () => {
  try {
    const keysToRemove = []
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i)
      if (!key) continue
      if (key === 'shipmentFilters' || key.startsWith('shipmentFilters_')) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach((key) => window.localStorage.removeItem(key))
  } catch (_e) {
    // ignore localStorage cleanup failures
  }
}

export async function impersonateClerkUser({
  session,
  signIn,
  setActive,
  setClerkActive,
  actingUserId,
  actorId,
  organizationId,
}) {
  if (!session || !signIn || !setActive || !actingUserId || !actorId || actingUserId === actorId) {
    return
  }

  if (!organizationId || INVALID_ORG_IDS.has(organizationId)) {
    throw new Error('Organization context is required for impersonation')
  }

  setImpersonationTargetOrgId(organizationId)
  markOrgSwitchTransition(organizationId)

  const tokenObject = await createActorToken(session, actingUserId, actorId)
  localStorage.removeItem('viewShipmentType')

  const { createdSessionId } = await signIn.create({
    strategy: 'ticket',
    ticket: tokenObject.token,
  })

  await setActive({ session: createdSessionId })

  if (setClerkActive) {
    await setClerkActive({ organization: organizationId })
  }

  clearShipmentFilterStorage()
  clearLocalStorage()
  localStorage.setItem(ORG_SWITCH_STORAGE_KEY, organizationId)
  window.location.replace('/landing-page')
}
